import { Icon } from "@iconify/react";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Box, Button, CircularProgress, Collapse, IconButton, Stack, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import gradient_bg from '../assets/backgrounds/gradient.png';
import { AppContext } from "../Base";
import Avatar from "../components/cards/Avatar";
import ChallengesList from "../components/Challenges/ChallengesList";
import ResultGaugeChart from "../components/charts/ResultGaugeChart";
import CoinIcon from "../components/icons/Coin";
import PointIcon from "../components/icons/Point";
import AppHeader from "../components/nav/AppHeader";
import BottomNav from "../components/nav/BottomNav";
import { getLevelFromPoints } from "../consts/avatarMap";
import { challenges } from "../consts/challenges";
import { displayNumber, sendReactGAPageView } from "../consts/helpers";
import useAuthUser from "../hooks/useAuthUser";

export default function Homepage() {
	const { pathname } = useLocation();
	useEffect(() => {
        sendReactGAPageView(pathname, 'I know, I care, I act');
    }, [pathname])

    var { isNewUser, checkAuth, userid }  = useAuthUser();
    const { state } = useLocation();
    const navigate = useNavigate();

    // User Information
    const username = useContext(AppContext).usernameState; 
    const points = useContext(AppContext).pointsState; 
    const coins = useContext(AppContext).coinsState; 
    const footprint = useContext(AppContext).footprintState;
    
    // Flags
    const [isLoading, setIsLoading] = useState(true);
    const [openProfile, setOpenProfile] = useState(false);
    const [openChallenge, setOpenChallenge] = useState(true);

    // Displayed Challenges
    const [challengesList, setChallenges] = useState(challenges);
    const [filteredChallenges, setFilteredChallenges] = useState([]);
    const [activeChallengeIds, setActiveChallengeIds] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');

    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
    };

    const handleFetchUserChallenges = () => {
        const body = {
            "httpMethod": "GET", // For routing
            "resourcePath": "/challenges", // For routing
            "uid": userid,
        };
        const url = process.env.REACT_APP_UPLOAD_LAMBDA;
        fetch(url, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        }).then(response => {
            return response.json();
        }).then(data => {
            let results = JSON.parse(data?.body || '{}');
            // console.log(results)
            let challengeDatesCompleted = results?.userChallenges;

            setChallenges(currChallenges => {
                for (const key in challengeDatesCompleted) {
                    if (currChallenges[key]) {
                        currChallenges[key].datesCompleted = challengeDatesCompleted[key]?.datesCompleted;
                        currChallenges[key].streakCount = challengeDatesCompleted[key]?.streakCount;
                    }
                }
                return currChallenges;
            })

            const activeChallenges = Object.fromEntries(
                Object.entries(challenges)
                    .filter(([key, value]) => 
                        Object.keys(challengeDatesCompleted).includes(key) 
                        && (value.type === 'weekly' || value.type === 'day')
                        && (value.streakCount > 0 )
                )
            );
            setActiveChallengeIds(Object.keys(activeChallenges));
        }).catch(error => {
            console.error('There has been a problem with your fetch operation:', error);
            return {};
        });
    };

    useEffect(() => {
        if (userid) {
            setIsLoading(false);
            handleFetchUserChallenges();
        }
    }, [userid])

    useEffect(() => {
        setSelectedCategory(state?.selectedCategory || '')
    }, [state])

    useEffect(() => {
        setFilteredChallenges(
            selectedCategory
            ? Object.entries(challengesList).filter(([_, cdetails]) => cdetails.category === selectedCategory)
            : Object.entries(challengesList)
        );
    }, [selectedCategory])

    useEffect(() => {
        if (!openChallenge) {
            checkAuth();
            handleFetchUserChallenges();
        }
    }, [openChallenge])

    return (
        <>
            <AppHeader />
            <Stack spacing={4}>
                {/* Profile */}
                <Box 
                    className='centered-container' 
                    sx={{ position: 'relative', borderRadius: 'var(--container-rad)', backgroundColor: 'var(--pri-purple)', p: 4, color: 'white', textAlign: 'center', background: `var(--pri-purple) url(${gradient_bg}) no-repeat center/cover` }}
                >
                { 
                    isLoading ?
                    <Box className='centered-container' sx={{ flex: 1, minHeight: '40vh' }}>
                        <CircularProgress color='inherit' />
                    </Box>
                    :
                    <>
                        { !isNewUser &&
                        <>
                            <Typography variant='body1'>
                                Welcome back{username && ` ${username}`}!
                            </Typography>
                            <Typography variant='body2'>
                                Here is your profile:
                            </Typography>
                        </>
                        }
                        <Box sx={{ py: 2 }}>
                            <Avatar level={isNewUser ? 100 : getLevelFromPoints(points)} style={{ maxWidth: 150 }} alt="Current Avatar" />
                        </Box>
                        <Typography variant='h4' sx={{ textShadow: '0 -4px 6px #00000055' }}>
                        {
                        isNewUser ?
                            'WHAT IS YOUR CARBON FOOTPRINT?'
                        :
                            <Avatar level={getLevelFromPoints(points)} getImage={false} />
                        }
                        </Typography>
                        { !isNewUser &&
                        <>
                            <Box className='centered-x'>
                                <PointIcon />
                                <Typography variant='subtitle1'>
                                    &emsp;{points} point{points!==1 ? 's' : ''}
                                </Typography>
                            </Box>

                            <Box className='centered-x'>
                                <CoinIcon />
                                <Typography variant='subtitle1'>
                                    &emsp;{coins} coin{coins!==1 ? 's' : ''}
                                </Typography>
                            </Box>

                            <Typography variant='subtitle1'>
                                <Icon icon="material-symbols:barefoot" color='var(--pri-green)'/>&emsp;{displayNumber(footprint, 2)} tCO<sub>2</sub>e/year
                            </Typography>

                            <Collapse in={openProfile}>
                                <Typography variant='body2'>
                                    Your Estimated Annual Carbon Footprint
                                </Typography>
                                <ResultGaugeChart resultValue={footprint} />
                                <Stack spacing={1} paddingTop={3}>
                                    <Button className="pill_btn pri_btn" onClick={() => navigate('/profile/edit')}>YOUR CARBON SCORECARD</Button>
                                    <Button className="pill_btn pri_btn" onClick={() => navigate('/profile/rewards')}>CLAIM REWARDS</Button>
                                    <Button className="pill_btn pri_btn" onClick={() => navigate('/profile/leaderboard')}>VIEW LEADERBOARD</Button>
                                </Stack>
                            </Collapse>
                            
                            <IconButton onClick={() => setOpenProfile(!openProfile)}>
                                {openProfile ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                            </IconButton>
                        </>
                        }
                    </>
                }
                </Box>

            {
                isNewUser ?
                <Button className="pill_btn pri_btn" onClick={() => navigate('/profile/edit')}>FIND OUT NOW</Button>
                :
                <Stack spacing={4} onClick={() => setOpenProfile(false)}>
                    <ChallengesList 
                        uid={userid} 
                        setOpenChallenge={setOpenChallenge} 
                        challengesList={challengesList} 
                        activeChallengeIds={activeChallengeIds} 
                        filteredChallenges={filteredChallenges} 
                        selectedCategory={selectedCategory} 
                        handleCategoryChange={handleCategoryChange} 
                    />
                </Stack>
            }
            </Stack>
            <BottomNav />
        </>
    )
}